import React, { useEffect, useState } from 'react';

import { InputAdornment, TextField } from '@mui/material';

type PercentFieldProps = {
  /** 現在の値（数値文字列、空文字=未入力） */
  value: string;
  /** 値変更ハンドラ */
  onChange: (value: string) => void;
  /** ラベル */
  label?: string;
  /** 無効状態 */
  disabled?: boolean;
  /** エラー表示 */
  error?: boolean;
  /** エラーメッセージ */
  helperText?: string;
  /** 必須表示 */
  required?: boolean;
};

/**
 * パーセント入力コンポーネント
 *
 * - 0〜100の範囲のみ入力可能
 * - 100以上を入れると確定時に自動で100になる
 * - 負数を入れると確定時に自動で0になる
 * - 入力状態でない場合は % を表示する
 */
const PercentField: React.FC<PercentFieldProps> = ({
  value,
  onChange,
  label,
  disabled = false,
  error = false,
  helperText,
  required = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    if (!isFocused) {
      setLocalValue(value);
    }
  }, [value, isFocused]);

  const handleFocus = () => {
    setIsFocused(true);
    setLocalValue(value);
  };

  const handleBlur = () => {
    setIsFocused(false);
    const cleaned = localValue.replace(/[^0-9-]/g, '');
    if (cleaned === '' || cleaned === '-') {
      // 負号だけなら0にクランプ
      if (cleaned === '-') {
        onChange('0');
      } else {
        onChange('');
      }
      return;
    }
    const num = parseInt(cleaned, 10);
    if (isNaN(num)) {
      onChange('');
    } else if (num > 100) {
      onChange('100');
    } else if (num < 0) {
      onChange('0');
    } else {
      onChange(String(num));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // 数値と負号のみ許可
    if (raw === '' || raw === '-' || /^-?\d+$/.test(raw)) {
      setLocalValue(raw);
    }
  };

  return (
    <TextField
      size="small"
      fullWidth
      label={label}
      required={required}
      error={error}
      helperText={helperText}
      disabled={disabled}
      value={isFocused ? localValue : value}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      slotProps={{
        input: {
          endAdornment:
            !isFocused && value ? <InputAdornment position="end">%</InputAdornment> : undefined,
        },
      }}
      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
    />
  );
};

export default PercentField;
